import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "disabled", "readOnly", "selected", "description", "name", "size", "textLabel", "title", "type", "truncatable", "block"];
import styled from 'foundations-theming/utils';
import devLogger from 'react-utils/devLogger';
import { Controllable } from '../Controllable/Controllable';
import { SelectableBox } from '../SelectableBox';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SelectableButtonWrapper = styled.div.withConfig({
  displayName: "SelectableButton__SelectableButtonWrapper",
  componentId: "sc-2exn7z-0"
})(props => {
  return Object.assign({
    display: 'flex',
    maxWidth: '200px',
    textAlign: 'center'
  }, props.$size === 'auto' && {
    display: 'inline-block',
    maxWidth: 'none',
    width: 'auto'
  }, props.$block && {
    maxWidth: 'none',
    width: '100%'
  }, props.$disabled && {
    cursor: 'not-allowed'
  }, {
    // Targets the top-element rendered by the SelectableBox child
    '& > div:first-child': {
      width: '100%'
    }
  });
});
const StyledInner = styled.div.withConfig({
  displayName: "SelectableButton__StyledInner",
  componentId: "sc-2exn7z-1"
})(props => {
  const theme = props.theme;
  const shorthandSize = toShorthandSize(props.$size || 'sm');
  return Object.assign({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  }, shorthandSize === 'lg' && {
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '180px',
    padding: theme.space[300]
  }, shorthandSize === 'sm' && {
    gap: theme.space[400],
    minHeight: '56px',
    padding: theme.space[300]
  }, props.$size === 'auto' && {
    gap: theme.space[400],
    padding: theme.space[400]
  }, {
    '& img': {
      transform: 'translateZ(0)',
      maxWidth: '100%'
    },
    '& svg': {
      marginLeft: theme.space[100]
    }
  });
});
const StyledLabel = styled.div.withConfig({
  displayName: "SelectableButton__StyledLabel",
  componentId: "sc-2exn7z-2"
})(props => {
  const theme = props.theme;
  const shorthandSize = toShorthandSize(props.$size || 'sm');
  return Object.assign({
    display: 'block',
    whiteSpace: 'normal',
    width: '100%'
  }, theme.typography['heading-100'], shorthandSize === 'lg' && {
    paddingTop: theme.space[400],
    marginBottom: theme.space[200]
  }, shorthandSize === 'sm' && Object.assign({
    marginLeft: 0,
    marginRight: 0
  }, props.$truncatable && {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }, props.$type !== 'default' && {
    paddingLeft: '39px'
  }), {
    lineHeight: theme.typography['heading-200'].lineHeight
  });
});
const StyledDescription = styled.span.withConfig({
  displayName: "SelectableButton__StyledDescription",
  componentId: "sc-2exn7z-3"
})(props => {
  const theme = props.theme;
  return Object.assign({}, theme.typography['body-100'], {
    display: 'block',
    whiteSpace: 'normal',
    marginTop: theme.space[100],
    color: theme.color['text-core-subtle']
  });
});
const Title = styled.span.withConfig({
  displayName: "SelectableButton__Title",
  componentId: "sc-2exn7z-4"
})({
  display: 'block',
  whiteSpace: 'normal',
  width: '100%'
});
const InternalSelectableButton = _ref => {
  let {
      children,
      disabled = false,
      readOnly = false,
      selected = false,
      description,
      name,
      size = 'sm',
      textLabel,
      title,
      type = 'default',
      truncatable = true,
      block = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (process.env.NODE_ENV !== 'production') {
    if (textLabel == null && title == null && description == null && children == null) {
      devLogger.warn({
        message: 'SelectableButton: Should provide either text through title and description, or children.',
        key: 'SelectableButton: content'
      });
    }
    if (textLabel) {
      devLogger.warn({
        message: `SelectableButton: The \`textLabel\` prop is deprecated. Use the \`title\` and \`description\` props instead.`,
        key: 'SelectableButton-textLabel'
      });
    }
    if (type === 'radio' && !name) {
      devLogger.warn({
        message: 'SelectableButton: If type is set to radio you must provide a name',
        key: 'SelectableButton: no name value'
      });
    }
  }
  const shorthandSize = toShorthandSize(size);
  let computedSelectionMarkLocation;
  if (type !== 'default') {
    computedSelectionMarkLocation = shorthandSize === 'lg' ? 'top-right' : 'left';
  }
  return /*#__PURE__*/_jsx(SelectableButtonWrapper, {
    $size: size,
    $block: block,
    $disabled: disabled,
    children: /*#__PURE__*/_jsx(SelectableBox, Object.assign({
      disabled: disabled,
      readOnly: readOnly,
      selected: selected,
      name: type === 'default' ? undefined : name,
      padded: false,
      selectionMarkLocation: computedSelectionMarkLocation,
      type: type,
      block: block
    }, rest, {
      children: /*#__PURE__*/_jsxs(StyledInner, {
        $size: size,
        children: [children, textLabel ? /*#__PURE__*/_jsx(StyledLabel, {
          as: "span",
          $size: size,
          $truncatable: truncatable,
          $type: type,
          children: textLabel
        }) : null, !textLabel && (title || description) && /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsxs(StyledLabel, {
            as: title && description ? 'div' : 'span',
            $size: size,
            $truncatable: truncatable,
            $type: type,
            children: [title && /*#__PURE__*/_jsx(Title, {
              children: title
            }), description && /*#__PURE__*/_jsx(StyledDescription, {
              $size: size,
              children: description
            })]
          })
        })]
      })
    }))
  });
};
export const UncontrolledSelectableButton = InternalSelectableButton;
export const SelectableButton = Controllable(InternalSelectableButton, ['selected']);